// Тонкий fetch-клієнт. Додає або X-Telegram-Init-Data (Mini App), або
// Authorization: Bearer (веб з JWT). Типи відповідей — з бекенду (apps/api/src/routes/*).

import { getInitData } from './telegram';
import { getToken, clearToken } from './auth';
import {
  startOffline,
  submitOffline,
  finishOffline,
  isLocalAttempt,
  isOfflineMode,
} from './offlineEngine';
import { getExplStyle } from './settings';
import {
  getMeta,
  setMeta,
  isPackReady,
  offlineCategoriesWithCount,
  type OfflinePack,
  type OutboxAttempt,
} from './offlineDb';

// У dev — Vite proxy кидає /api → http://localhost:3000.
// У production задаємо VITE_API_URL у env (наприклад, https://api.example.com).
const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

function authHeaders(): Record<string, string> {
  // Пріоритет — Mini App initData. Якщо немає — пробуємо JWT з localStorage.
  const initData = getInitData();
  if (initData) return { 'X-Telegram-Init-Data': initData };
  const token = getToken();
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Content-Type: application/json додаємо ТІЛЬКИ якщо реально шлемо body.
  // Інакше Fastify падає: "Body cannot be empty when content-type is set to 'application/json'".
  const hasBody = init?.body !== undefined && init.body !== null;
  const headers: Record<string, string> = {
    ...authHeaders(),
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (hasBody && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    const msg = (body as { message?: string })?.message ?? `HTTP ${res.status}`;
    // 401 з JWT означає що токен прострочений/невалідний — чистимо щоб юзер заново логінився
    if (res.status === 401 && getToken()) clearToken();
    throw new ApiError(res.status, msg, body);
  }
  return res.json() as Promise<T>;
}

// ===== Типи відповідей =====

export type Category = {
  id: number;
  slug: string;
  nameUk: string;
  description: string | null;
  iconUrl: string | null;
  questionCount: number;
};

export type ExamMode =
  | 'CATEGORY'
  | 'OFFICIAL_20'
  | 'RANDOM_20'
  | 'HARDEST_100'
  | 'MISTAKES'
  | 'BOOKMARKS'
  | 'MARATHON';

export type Question = {
  id: number;
  textUk: string;
  imageUrl: string | null;
  answers: Array<{ id: number; textUk: string; order: number }>;
};

export type StartAttemptResponse = {
  attemptId: string;
  mode: ExamMode;
  timeLimitSeconds: number;
  questions: Question[];
};

export type SubmitAnswerResponse = {
  isCorrect: boolean;
  correctAnswerId: number | null;
  explanation: string | null;
  explanationCasual?: string | null;
};

export type FinishAttemptResponse = {
  score: number;
  total: number;
  passed: boolean;
  timeSpentMs?: number;
  xpEarned: number;
  newAchievements: string[];
};

export type Role = 'USER' | 'ADMIN';
export type LicenseCategory =
  | 'A1' | 'A' | 'B1' | 'B' | 'C1' | 'C' | 'D1' | 'D'
  | 'BE' | 'C1E' | 'CE' | 'D1E' | 'DE'
  | 'T';
export const LICENSE_CATEGORIES: LicenseCategory[] = [
  'A1', 'A', 'B1', 'B', 'C1', 'C', 'D1', 'D',
  'BE', 'C1E', 'CE', 'D1E', 'DE',
  'T',
];

export type MeResponse = {
  user: {
    id: string;
    telegramId: string | null;
    username: string | null;
    firstName: string | null;
    photoUrl: string | null;
    xp: number;
    level: number;
    streakDays: number;
    role: Role;
    licenseCategory: LicenseCategory;
    isPremium: boolean;
    premiumUntil: string | null;
    createdAt: string;
  };
  progress: { currentLevelXp: number; nextLevelXp: number };
  stats: {
    totalAttempts: number;
    passedCount: number;
    passRate: number;
    totalAnswers: number;
    correctAnswers: number;
    wrongAnswers: number;
    accuracy: number;
    officialAttempts: number;
    officialPassed: number;
    averageOfficialScore: number;
    bookmarksCount: number;
    mistakesCount: number;
  };
  achievements: Array<{
    slug: string;
    nameUk: string;
    description: string;
    iconUrl: string | null;
    unlockedAt: string;
  }>;
};

export type AttemptHistoryItem = {
  id: string;
  mode: ExamMode;
  category: { nameUk: string; slug: string } | null;
  score: number;
  total: number;
  passed: boolean;
  timeSpentMs: number | null;
  finishedAt: string;
};

export type LeaderboardItem = {
  rank: number;
  userId: string;
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  level: number;
  xp: number;
  isPremium: boolean;
};

export type LeaderboardResponse = {
  scope: 'all' | 'week';
  items: LeaderboardItem[];
  myRank: number | null;
  note?: string;
};

// ===== Битви 1v1 =====

export type MatchStatus = 'WAITING' | 'ACTIVE' | 'FINISHED' | 'CANCELLED';

export type MatchPlayerSummary = {
  user: {
    id: string;
    username: string | null;
    firstName: string | null;
    photoUrl: string | null;
    level: number;
  };
  score: number;
  answeredCount: number;
  finishedAt: string | null;
};

export type MatchState = {
  code: string;
  status: MatchStatus;
  category: { id: number; slug: string; nameUk: string } | null;
  questionCount: number;
  timeLimitSec: number;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  winnerId: string | null;
  isDraw: boolean;
  questions: Question[];
  me: { score: number; answeredCount: number; finishedAt: string | null } | null;
  opponent: MatchPlayerSummary | null;
};

export type MatchListItem = {
  code: string;
  status: MatchStatus;
  category: { nameUk: string; slug: string } | null;
  questionCount: number;
  createdAt: string;
  finishedAt: string | null;
  myScore: number;
  opponentScore: number | null;
  opponent: { id: string; username: string | null; firstName: string | null; photoUrl: string | null } | null;
  isWinner: boolean;
  isDraw: boolean;
};

// ===== Адмінка =====

export type AdminStats = {
  users: {
    total: number;
    newLast7Days: number;
    newLast30Days: number;
    dau: number;
    wau: number;
    mau: number;
    onlineNow?: number;
    online15?: number;
    onlineHour?: number;
  };
  platforms?: {
    telegram: { total: number; wau: number };
    site: { total: number; wau: number };
    app: { total: number; wau: number };
  };
  attempts: { total: number; today: number; week: number; passed: number; passRate: number };
  questions: { total: number; active: number; withImage: number; withCorrect: number; unmarked: number };
  bookmarks: number;
  matches: { total: number; active: number; finished: number; draws: number };
  premium: { active: number; expiringIn7Days: number };
  revenue: {
    today: number;
    week: number;
    month: number;
    total: number;
    purchasesTotal: number;
    purchasesToday: number;
    purchasesWeek: number;
    purchasesMonth: number;
    avg30d: number;
    byPlan: Array<{ months: number; count: number; sumUah: number }>;
  };
  conversion: {
    registered: number;
    triedExam: number;
    boughtPremium: number;
    tryRate: number;
    buyRate: number;
    overallRate: number;
  };
  topCategories: Array<{ categoryId: number | null; slug: string | null; nameUk: string; attempts: number }>;
  mostMistaken: Array<{ questionId: number; textUk: string; order: number; categoryName: string; wrongCount: number }>;
};

export type BroadcastResult = {
  total: number;
  sent: number;
  blocked: number;
  failed: number;
  errors: Array<{ telegramId: string; reason: string }>;
};

export type ReminderUser = {
  id: string;
  telegramId: string | null;
  firstName: string | null;
  username: string | null;
};

export type AdminAnswer = {
  id: number;
  questionId: number;
  textUk: string;
  isCorrect: boolean;
  order: number;
};

export type AdminQuestion = {
  id: number;
  categoryId: number;
  textUk: string;
  imageUrl: string | null;
  explanation: string | null;
  explanationCasual: string | null;
  difficulty: number;
  isActive: boolean;
  version: number;
  licenseCategories: LicenseCategory[];
  order: number;
  category: { id: number; slug: string; nameUk: string };
  answers?: AdminAnswer[];
  _count?: { answers: number };
};

export type AdminQuestionList = {
  items: AdminQuestion[];
  total: number;
  limit: number;
  offset: number;
};

export type AdminCategory = {
  id: number;
  slug: string;
  nameUk: string;
  description: string | null;
  iconUrl: string | null;
  order: number;
  isActive: boolean;
  _count: { questions: number };
};

export type AdminUser = {
  id: string;
  telegramId: string | null;
  email: string | null;
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  role: Role;
  xp: number;
  level: number;
  streakDays: number;
  premiumUntil: string | null;
  botBlockedAt: string | null;
  lastActiveAt: string;
  createdAt: string;
  referralBonusesGranted: number;
  refereeCount: number;
  source: {
    type: 'partner' | 'referral' | 'site' | 'seo' | 'organic';
    label: string;
    sublabel?: string;
  };
};

export type UserStatusFilter = 'all' | 'active' | 'inactive' | 'blocked' | 'premium';
export type UserPeriodFilter = 'all' | 'today' | 'week' | 'month';

export type AdminUserList = {
  items: AdminUser[];
  total: number;
  limit: number;
  offset: number;
  filters: {
    status: UserStatusFilter;
    period: UserPeriodFilter;
    search: string | null;
  };
};

export type AdminUserStats = {
  total: number;
  byStatus: { active: number; inactive: number; blocked: number; premium: number };
  newRegistrations: { today: number; week: number; month: number };
};

export type AnswerInput = {
  id?: number;
  textUk: string;
  isCorrect: boolean;
  order: number;
};

export type QuestionCreateInput = {
  categoryId: number;
  textUk: string;
  imageUrl?: string | null;
  explanation?: string | null;
  explanationCasual?: string | null;
  difficulty?: number;
  licenseCategories?: LicenseCategory[];
  answers: AnswerInput[];
};

export type QuestionUpdateInput = Partial<QuestionCreateInput> & { isActive?: boolean };

// ===== API методи =====

export const api = {
  categories: async (): Promise<Category[]> => {
    if (!navigator.onLine) {
      // Офлайн: беремо категорії з завантаженого пакета (з підрахунком питань)
      if (await isPackReady()) {
        const off = await offlineCategoriesWithCount();
        return off.map((c) => ({
          id: c.id,
          slug: c.slug,
          nameUk: c.nameUk,
          description: null,
          iconUrl: c.iconUrl,
          questionCount: c.questionCount,
        }));
      }
      const cached = await getMeta<Category[]>('categories');
      if (cached) return cached;
    }
    const cats = await request<Category[]>('/api/v1/categories');
    try {
      await setMeta('categories', cats);
    } catch {
      /* ignore */
    }
    return cats;
  },

  // Публічні лінки (канал новин, підтримка) — з AppSetting, ті самі що в боті
  links: () => request<{ newsChannelUrl: string; supportUrl: string }>('/api/v1/links'),

  // Довідник дорожніх знаків (вкладка /signs)
  signs: () =>
    request<{
      groups: Array<{
        code: string;
        name: string;
        signs: Array<{
          id: number;
          code: string;
          groupCode: string;
          groupName: string;
          nameUk: string;
          imageUrl: string;
        }>;
      }>;
    }>('/api/v1/signs'),
  signDetail: (code: string) =>
    request<{
      id: number;
      code: string;
      groupCode: string;
      groupName: string;
      nameUk: string;
      descriptionUk: string;
      imageUrl: string;
    }>(`/api/v1/signs/${encodeURIComponent(code)}`),

  updateMe: (input: { licenseCategory?: LicenseCategory }) =>
    request<{ licenseCategory: LicenseCategory }>('/api/v1/me', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),


  // Офлайн: якщо нема мережі й пакет завантажено — стартуємо тест із локальної бази.
  startAttempt: async (input: { mode: ExamMode; categoryId?: number }): Promise<StartAttemptResponse> => {
    if (!navigator.onLine && isOfflineMode(input.mode) && (await isPackReady())) {
      return startOffline(input.mode, input.categoryId);
    }
    return request<StartAttemptResponse>('/api/v1/attempts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  submitAnswer: async (
    attemptId: string,
    input: { questionId: number; answerId: number | null; timeMs: number },
  ): Promise<SubmitAnswerResponse> => {
    const res = isLocalAttempt(attemptId)
      ? await submitOffline(attemptId, input)
      : await request<SubmitAnswerResponse>(`/api/v1/attempts/${attemptId}/answer`, {
          method: 'POST',
          body: JSON.stringify(input),
        });
    // Стиль пояснення обирається в налаштуваннях: «дружбан» підміняє текст,
    // якщо casual-варіант існує (сторінки тесту нічого не знають про стилі).
    if (getExplStyle() === 'casual' && res.explanationCasual) {
      return { ...res, explanation: res.explanationCasual };
    }
    return res;
  },

  finishAttempt: (attemptId: string): Promise<FinishAttemptResponse> => {
    if (isLocalAttempt(attemptId)) return finishOffline(attemptId);
    return request<FinishAttemptResponse>(`/api/v1/attempts/${attemptId}/finish`, {
      method: 'POST',
    });
  },

  // /me — онлайн кешуємо (для офлайн-показу + движок бере звідси premium/категорію прав)
  me: async (): Promise<MeResponse> => {
    if (!navigator.onLine) {
      const cached = await getMeta<MeResponse>('me');
      if (cached) return cached;
    }
    const me = await request<MeResponse>('/api/v1/me');
    try {
      await setMeta('me', me);
      await setMeta('licenseCategory', me.user.licenseCategory ?? null);
      await setMeta('premiumUntil', me.user.premiumUntil ?? null);
    } catch {
      /* IndexedDB недоступний — не критично */
    }
    return me;
  },

  myAttempts: async (): Promise<AttemptHistoryItem[]> => {
    if (!navigator.onLine) {
      const cached = await getMeta<AttemptHistoryItem[]>('myAttempts');
      return cached ?? [];
    }
    const r = await request<AttemptHistoryItem[]>('/api/v1/me/attempts');
    try {
      await setMeta('myAttempts', r);
    } catch {
      /* ignore */
    }
    return r;
  },

  // Оцінка пояснення (👍/👎 + скарга) — для адмінки, щоб бачити слабкі пояснення
  explanations: {
    vote: (input: {
      questionId: number;
      style: 'instructor' | 'casual';
      vote: 1 | -1;
      comment?: string;
    }) =>
      request<{ ok: true; up: number; down: number }>('/api/v1/explanations/vote', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    votes: (questionId: number, style: 'instructor' | 'casual') =>
      request<{ up: number; down: number; myVote: number | null }>(
        `/api/v1/explanations/votes?questionId=${questionId}&style=${style}`,
      ),
  },

  // ===== Офлайн: пакет питань + синхронізація проходжень =====
  offline: {
    // Тягне пакет; при 304 (не змінився) повертає null. If-None-Match — поточна версія.
    pack: async (etag?: string | null): Promise<OfflinePack | null> => {
      const headers: Record<string, string> = { ...authHeaders() };
      if (etag) headers['If-None-Match'] = `"${etag}"`;
      const res = await fetch(`${BASE_URL}/api/v1/offline/pack`, { headers });
      if (res.status === 304) return null;
      if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
      return res.json() as Promise<OfflinePack>;
    },
    sync: (attempts: OutboxAttempt[]) =>
      request<{ synced: number; duplicate: number; invalid: number }>(
        '/api/v1/offline/attempts/sync',
        { method: 'POST', body: JSON.stringify({ attempts }) },
      ),
  },

  toggleBookmark: (questionId: number) =>
    request<{ bookmarked: boolean }>('/api/v1/bookmarks/toggle', {
      method: 'POST',
      body: JSON.stringify({ questionId }),
    }),
  checkBookmark: (questionId: number) =>
    request<{ bookmarked: boolean }>(`/api/v1/bookmarks/check?questionId=${questionId}`),
  myBookmarks: () =>
    request<Array<{
      questionId: number;
      createdAt: string;
      question: {
        id: number;
        textUk: string;
        imageUrl: string | null;
        order: number;
        category: { nameUk: string; slug: string };
      };
    }>>('/api/v1/me/bookmarks'),


  leaderboard: (scope: 'all' | 'week' = 'all', limit = 20) =>
    request<LeaderboardResponse>(`/api/v1/leaderboard?scope=${scope}&limit=${limit}`),

  // ===== Битви 1v1 =====
  battles: {
    create: (input: { categoryId?: number; questionCount?: number }) =>
      request<{ code: string; id: string; status: MatchStatus }>('/api/v1/matches', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    join: (code: string) =>
      request<{ code: string; joined: true; alreadyIn: boolean }>(
        `/api/v1/matches/${code}/join`,
        { method: 'POST' },
      ),
    state: (code: string) => request<MatchState>(`/api/v1/matches/${code}`),
    answer: (code: string, input: { questionId: number; answerId: number | null; timeMs: number }) =>
      request<{ isCorrect: boolean; correctAnswerId: number | null }>(
        `/api/v1/matches/${code}/answer`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    finish: (code: string) =>
      request<{
        finished: true;
        alreadyFinished: boolean;
        matchStatus: MatchStatus;
        score?: number;
        total?: number;
        winnerId?: string | null;
        isDraw?: boolean;
        xpEarned?: number;
      }>(`/api/v1/matches/${code}/finish`, { method: 'POST' }),
    myMatches: () => request<MatchListItem[]>('/api/v1/me/matches'),
    cancel: (code: string) =>
      request<{ cancelled: true }>(`/api/v1/matches/${code}`, { method: 'DELETE' }),
  },

  referral: () =>
    request<{
      refereeCount: number; // активні (пройшли хоча б 1 тест)
      refereesTotal: number; // всі, хто зайшов за посиланням
      bonusesGranted: number;
      totalDaysEarned: number;
      threshold: number;
      currentTierProgress: number;
      remainingToNextBonus: number;
      refLink: string | null;
      contest: null | {
        endLabel: string;
        resultsLabel: string;
        prizeTop: string;
        prizeDraw: string;
        invited: number;
        active: number;
        rank: number | null;
        participants: number;
      };
      bonusDays: number;
      inviteeBonusDays: number;
      referees: Array<{
        firstName: string | null;
        lastNameInitial: string | null;
        joinedAt: string;
        isPremium: boolean;
        tookTest: boolean;
      }>;
    }>('/api/v1/me/referral'),

  premium: {
    get: async () => {
      const fetchIt = () =>
        request<{
          isPremium: boolean;
          premiumUntil: string | null;
          paymentReady: boolean;
          specialPricing?: boolean;
          specialLabel?: string | null;
          // Google Play (RevenueCat) — для нативного Android-додатка
          appUserId?: string;
          revenuecatPublicKey?: string | null;
          plans: Array<{
            id: string;
            months: number;
            priceUah: number;
            basePriceUah?: number;
            overridden?: boolean;
          }>;
        }>('/api/v1/premium');
      if (!navigator.onLine) {
        const cached = await getMeta<Awaited<ReturnType<typeof fetchIt>>>('premium');
        if (cached) return cached;
      }
      const r = await fetchIt();
      try {
        await setMeta('premium', r);
      } catch {
        /* ignore */
      }
      return r;
    },
    // Разовий бонус за встановлення Android-додатка (+3 дні Premium)
    appBonus: () =>
      request<{ granted: boolean; days?: number; premiumUntil: string | null }>(
        '/api/v1/premium/app-bonus',
        { method: 'POST' },
      ),
    checkPromocode: (code: string, plan: '1m' | '3m' | '6m') =>
      request<{
        code: string;
        discountPercent: number;
        originalPrice: number;
        discount: number;
        finalPrice: number;
      }>('/api/v1/premium/promocode/check', {
        method: 'POST',
        body: JSON.stringify({ code, plan }),
      }),
    checkout: (plan: '1m' | '3m' | '6m', promocode?: string) =>
      request<{ redirectUrl: string; orderReference: string }>(
        '/api/v1/premium/checkout',
        { method: 'POST', body: JSON.stringify(promocode ? { plan, promocode } : { plan }) },
      ),
  },

  // Web Push — сповіщення у застосунок (веб / Android TWA)
  push: {
    publicKey: () => request<{ publicKey: string | null }>('/api/v1/push/public-key'),
    subscribe: (input: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
      request<{ ok: true }>('/api/v1/push/subscribe', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    unsubscribe: (endpoint: string) =>
      request<{ ok: true }>('/api/v1/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      }),
    test: () => request<{ sent: number }>('/api/v1/push/test', { method: 'POST' }),
    // FCM-токен від нативної апки (Capacitor)
    deviceToken: (token: string, platform: 'android' | 'ios' = 'android') =>
      request<{ ok: true }>('/api/v1/push/device-token', {
        method: 'POST',
        body: JSON.stringify({ token, platform }),
      }),
  },

  admin: {
    listQuestions: (params: {
      categoryId?: number;
      isActive?: 'true' | 'false' | 'all';
      search?: string;
      licenseCategory?: LicenseCategory;
      licenseScope?: 'all' | 'specific' | 'universal';
      limit?: number;
      offset?: number;
    } = {}) => {
      const usp = new URLSearchParams();
      if (params.categoryId) usp.set('categoryId', String(params.categoryId));
      if (params.isActive) usp.set('isActive', params.isActive);
      if (params.search) usp.set('search', params.search);
      if (params.licenseCategory) usp.set('licenseCategory', params.licenseCategory);
      if (params.licenseScope && params.licenseScope !== 'all') usp.set('licenseScope', params.licenseScope);
      if (params.limit) usp.set('limit', String(params.limit));
      if (params.offset) usp.set('offset', String(params.offset));
      const qs = usp.toString();
      return request<AdminQuestionList>(`/api/v1/admin/questions${qs ? '?' + qs : ''}`);
    },
    getQuestion: (id: number) => request<AdminQuestion & { answers: AdminAnswer[] }>(`/api/v1/admin/questions/${id}`),
    // id + categoryId усіх питань у порядку списку — для навігації та лічильників (загальний + у темі)
    questionNavIds: () =>
      request<{ items: Array<{ id: number; c: number }> }>('/api/v1/admin/questions/nav-ids'),
    createQuestion: (input: QuestionCreateInput) =>
      request<AdminQuestion & { answers: AdminAnswer[] }>('/api/v1/admin/questions', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateQuestion: (id: number, input: QuestionUpdateInput) =>
      request<AdminQuestion & { answers: AdminAnswer[] }>(`/api/v1/admin/questions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    deleteQuestion: (id: number) =>
      request<{ ok: true }>(`/api/v1/admin/questions/${id}`, { method: 'DELETE' }),
    markCorrect: (id: number, answerId: number | null) =>
      request<{ ok: true; correctAnswerId: number | null }>(
        `/api/v1/admin/questions/${id}/correct`,
        { method: 'PATCH', body: JSON.stringify({ answerId }) },
      ),

    listCategories: () => request<AdminCategory[]>('/api/v1/admin/categories'),
    createCategory: (input: { slug: string; nameUk: string; description?: string | null; order?: number }) =>
      request<AdminCategory>('/api/v1/admin/categories', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateCategory: (id: number, input: Partial<{ slug: string; nameUk: string; description: string | null; order: number; isActive: boolean }>) =>
      request<AdminCategory>(`/api/v1/admin/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),

    stats: () => request<AdminStats>('/api/v1/admin/stats'),

    broadcast: (input: {
      text: string;
      parseMode?: 'HTML' | 'MarkdownV2' | 'Markdown';
      button?: { text: string; url: string };
      testOnly?: boolean;
    }) =>
      request<BroadcastResult>('/api/v1/admin/broadcast', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    triggerDailyReminders: () =>
      request<{
        candidates: number;
        sent: number;
        blocked: number;
        failed: number;
        sentUsers: ReminderUser[];
        blockedUsers: ReminderUser[];
        failedUsers: Array<ReminderUser & { reason: string }>;
      }>('/api/v1/admin/notifications/daily', { method: 'POST' }),

    listUsers: (
      params: {
        search?: string;
        status?: UserStatusFilter;
        period?: UserPeriodFilter;
        limit?: number;
        offset?: number;
      } = {},
    ) => {
      const usp = new URLSearchParams();
      if (params.search) usp.set('search', params.search);
      if (params.status && params.status !== 'all') usp.set('status', params.status);
      if (params.period && params.period !== 'all') usp.set('period', params.period);
      if (params.limit) usp.set('limit', String(params.limit));
      if (params.offset) usp.set('offset', String(params.offset));
      const qs = usp.toString();
      return request<AdminUserList>(`/api/v1/admin/users${qs ? '?' + qs : ''}`);
    },
    userStats: () => request<AdminUserStats>('/api/v1/admin/users/stats'),
    probeBlockedUsers: () =>
      request<{
        checked: number;
        active: number;
        newlyBlocked: number;
        otherErrors: number;
        durationMs: number;
      }>('/api/v1/admin/users/probe-blocked', { method: 'POST' }),
    updateUser: (id: string, input: { role?: Role }) =>
      request<{ id: string; role: Role; username: string | null; firstName: string | null }>(
        `/api/v1/admin/users/${id}`,
        { method: 'PATCH', body: JSON.stringify(input) },
      ),
    setUserPremium: (
      id: string,
      input: { months: number } | { days: number; isTrial?: boolean },
    ) =>
      request<{ id: string; premiumUntil: string | null }>(`/api/v1/admin/users/${id}/premium`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    feedback: (source?: string) =>
      request<{
        stats: { total: number; avg: number; dist: Record<number, number> };
        sources: Array<{ source: string; count: number }>;
        items: Array<{
          id: string;
          rating: number;
          comment: string | null;
          source: string;
          createdAt: string;
          updatedAt: string;
          user: {
            id: string;
            firstName: string | null;
            username: string | null;
            telegramId: string | null;
            email: string | null;
          };
        }>;
      }>(`/api/v1/admin/feedback${source ? `?source=${encodeURIComponent(source)}` : ''}`),

    listSettings: () => request<Array<{ key: string; value: string }>>('/api/v1/admin/settings'),
    updateSetting: (key: string, value: string) =>
      request<{ key: string; value: string }>('/api/v1/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({ key, value }),
      }),

    listPurchases: (show: 'active' | 'all' = 'active') =>
      request<
        Array<{
          id: string;
          orderReference: string;
          status: string;
          months: number;
          amountUah: number;
          clientAccountId: string | null;
          createdAt: string;
          approvedAt: string | null;
          rawCallback: unknown;
          user: {
            id: string;
            telegramId: string | null;
            username: string | null;
            firstName: string | null;
            isPremium: boolean;
          } | null;
        }>
      >(`/api/v1/admin/purchases?show=${show}`),
    pushBroadcast: (input: {
      title: string;
      body: string;
      url?: string;
      tag?: string;
      testOnly?: boolean;
    }) =>
      request<{ recipients: number; delivered: number; failed: number }>(
        '/api/v1/admin/push/broadcast',
        { method: 'POST', body: JSON.stringify(input) },
      ),
    referrals: (range: 'contest' | 'all' = 'all') =>
      request<{
        range: string;
        totals: { referrers: number; invited: number; active: number };
        leaderboard: Array<{
          id: string;
          name: string | null;
          username: string | null;
          invited: number;
          active: number;
          suspicious: number;
          friends: Array<{
            id: string;
            name: string | null;
            username: string | null;
            joinedAt: string;
            tests: number;
            answers: number;
            active: boolean;
            suspicious: boolean;
          }>;
        }>;
      }>(`/api/v1/admin/referrals?range=${range}`),
    explanations: (sort: 'problems' | 'likes' = 'problems') =>
      request<{
        totals: { up: number; down: number; total: number };
        items: Array<{
          questionId: number;
          style: string;
          score: number;
          votes: number;
          downs: number;
          textUk: string;
          categoryName: string;
          explanation: string | null;
          comments: string[];
        }>;
      }>(`/api/v1/admin/explanations?sort=${sort}`),
    assignPurchase: (id: string, userId: string, months?: number) =>
      request<{ ok: true }>(`/api/v1/admin/purchases/${id}/assign`, {
        method: 'POST',
        body: JSON.stringify(months ? { userId, months } : { userId }),
      }),

    listPromocodes: () =>
      request<
        Array<{
          id: number;
          code: string;
          discountPercent: number;
          maxUses: number | null;
          usedCount: number;
          actualUses: number;
          validUntil: string | null;
          isActive: boolean;
          comment: string | null;
          createdAt: string;
          // Партнерська програма
          partnerName: string | null;
          partnerContact: string | null;
          commissionPercent: number;
          partnerToken: string | null;
          partnerSlug: string | null;
          // Кастомні ціни для трафіку з посилання (null = стандартна)
          priceOverride1m: number | null;
          priceOverride3m: number | null;
          priceOverride6m: number | null;
          // Промокод-канал (юзер ввів код)
          paidUsesCount: number;
          totalRevenueUah: number;
          totalSavedUah: number;
          commissionUah: number;
          conversionRate: number;
          lastUseAt: string | null;
          // Acquisition-канал (прийшов через посилання, купив без коду)
          acquiredPaidCount: number;
          acquiredRevenueUah: number;
          acquiredCommissionUah: number;
          // Сума обох — реальна виплата партнеру
          totalCommissionUah: number;
          // Журнал виплат: скільки вже виплачено і скільки лишилось
          paidOutUah: number;
          toPayUah: number;
        }>
      >('/api/v1/admin/promocodes'),
    addPartnerPayout: (id: number, amountUah: number, note?: string) =>
      request<{ id: number; amountUah: number; paidOutUah: number }>(
        `/api/v1/admin/promocodes/${id}/payouts`,
        { method: 'POST', body: JSON.stringify(note ? { amountUah, note } : { amountUah }) },
      ),
    listPartnerPayouts: (id: number) =>
      request<Array<{ id: number; amountUah: number; note: string | null; createdAt: string }>>(
        `/api/v1/admin/promocodes/${id}/payouts`,
      ),
    promocodeDetails: (id: number) =>
      request<{
        promocode: {
          id: number;
          code: string;
          discountPercent: number;
          commissionPercent: number;
          partnerName: string | null;
          partnerContact: string | null;
          comment: string | null;
          isActive: boolean;
          createdAt: string;
        };
        summary: {
          actualUses: number;
          paidUsesCount: number;
          totalRevenueUah: number;
          totalSavedUah: number;
          commissionUah: number;
          conversionRate: number;
        };
        rows: Array<{
          useId: string;
          usedAt: string;
          amountSavedUah: number;
          user: {
            id: string;
            telegramId: string | null;
            username: string | null;
            firstName: string | null;
            lastName: string | null;
            isPremium: boolean;
            premiumUntil: string | null;
          };
          purchase: {
            id: string;
            status: string;
            amountUah: number;
            months: number;
            createdAt: string;
            approvedAt: string | null;
          } | null;
        }>;
      }>(`/api/v1/admin/promocodes/${id}/details`),
    // Скачує CSV з браузера. Не можемо просто <a href> бо API вимагає auth-хедер.
    downloadPromocodeCsv: async (id: number, code: string) => {
      const headers: Record<string, string> = {};
      const initData = getInitData();
      if (initData) headers['X-Telegram-Init-Data'] = initData;
      else {
        const token = getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(`${BASE_URL}/api/v1/admin/promocodes/${id}/export.csv`, { headers });
      if (!res.ok) throw new ApiError(res.status, 'Не вдалося завантажити CSV');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `promocode_${code}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    createPromocode: (input: {
      code: string;
      discountPercent: number;
      maxUses?: number | null;
      validUntil?: string | null;
      comment?: string | null;
      isActive?: boolean;
      partnerName?: string | null;
      partnerContact?: string | null;
      commissionPercent?: number;
      partnerSlug?: string | null;
      priceOverride1m?: number | null;
      priceOverride3m?: number | null;
      priceOverride6m?: number | null;
    }) =>
      request<{ id: number; code: string }>('/api/v1/admin/promocodes', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updatePromocode: (
      id: number,
      input: {
        discountPercent?: number;
        maxUses?: number | null;
        validUntil?: string | null;
        comment?: string | null;
        isActive?: boolean;
        partnerName?: string | null;
        partnerContact?: string | null;
        commissionPercent?: number;
        partnerSlug?: string | null;
        priceOverride1m?: number | null;
        priceOverride3m?: number | null;
        priceOverride6m?: number | null;
      },
    ) =>
      request<{ id: number; code: string }>(`/api/v1/admin/promocodes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    deletePromocode: (id: number) =>
      request<{ ok: true }>(`/api/v1/admin/promocodes/${id}`, { method: 'DELETE' }),

    quizRandom: (categoryId?: number) => {
      const qs = categoryId ? `?categoryId=${categoryId}` : '';
      return request<{
        questionId: number;
        questionText: string;
        questionTextFull: string;
        imageUrl: string | null;
        category: { id: number; nameUk: string };
        explanation: string | null;
        options: Array<{ text: string; textFull: string; isCorrect: boolean }>;
        correctIndex: number;
        copyText: string;
      } | { error: string }>(`/api/v1/admin/quiz/random${qs}`);
    },
    quizPublish: (questionId: number, channelId?: string) =>
      request<{ ok: true; channelId: string; messageId: number; warning?: string } | { error: string; message: string; hint?: string }>(
        '/api/v1/admin/quiz/publish',
        {
          method: 'POST',
          body: JSON.stringify(channelId ? { questionId, channelId } : { questionId }),
        },
      ),
    quizAutoGet: () =>
      request<{
        enabled: boolean;
        times: string[];
        categoryId: number | null;
        channelId: string | null;
        defaultChannelId: string | null;
        lastPublishedAt: string | null;
        lastError: string | null;
      }>('/api/v1/admin/quiz/auto-config'),
    quizAutoSave: (input: {
      enabled: boolean;
      times: string[];
      categoryId: number | null;
      channelId: string | null;
    }) =>
      request<{ ok: true } | { error: string; message: string }>(
        '/api/v1/admin/quiz/auto-config',
        { method: 'POST', body: JSON.stringify(input) },
      ),
    regeneratePromocodeToken: (id: number) =>
      request<{ id: number; partnerToken: string | null }>(
        `/api/v1/admin/promocodes/${id}/regenerate-token`,
        { method: 'POST' },
      ),

    uploadImage: async (file: File): Promise<{ url: string; size: number }> => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE_URL}/api/v1/admin/upload`, {
        method: 'POST',
        body: fd,
        // Не виставляємо Content-Type — браузер сам додасть boundary для multipart
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(res.status, (body as { message?: string }).message ?? 'Upload failed', body);
      }
      return res.json();
    },
  },
};

// ===== Auth (для веб-версії через Telegram Login Widget) =====

export type LoginWidgetData = {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

export type LoginResponse = {
  token: string;
  user: {
    id: string;
    telegramId: string | null;
    username: string | null;
    firstName: string | null;
    role: Role;
  };
};

export const auth = {
  loginWithTelegram: (data: LoginWidgetData) =>
    request<LoginResponse>('/api/v1/auth/telegram', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  // Email OTP: крок 1 — запит коду на пошту
  requestEmailCode: (email: string) =>
    request<{ ok: true; ttlSeconds: number } | { error: string; message: string }>(
      '/api/v1/auth/email/request',
      { method: 'POST', body: JSON.stringify({ email }) },
    ),
  // Email OTP: крок 2 — перевірка коду, повертає JWT
  verifyEmailCode: (email: string, code: string) =>
    request<LoginResponse>('/api/v1/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),
  // Вхід кодом з Telegram-бота (Android-додаток): бот видає код по /start appcode
  verifyAppCode: (code: string) =>
    request<LoginResponse>('/api/v1/auth/app-code/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  // Google Sign-In: шлемо ID-token з GIS, отримуємо JWT
  verifyGoogle: (idToken: string) =>
    request<LoginResponse>('/api/v1/auth/google/verify', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    }),
};
